/**
 * applyDraft
 *
 * Sequences the wizard draft onto the real, already-authorized API operations.
 * This is the one place the manual wizard's Hours and Reminders steps actually
 * persist — today those steps only write to the onboarding-draft blob, never to
 * PUT /business-hours or PUT /reminder-policy. Calling this once at the end of
 * onboarding (and from the concierge's confirm action) fixes that gap for both
 * paths, without inventing a new bulk-write endpoint whose partial-failure
 * semantics we'd have to design from scratch — each step below reuses the same
 * endpoint, dedupe rules, and validation the corresponding Settings tab uses.
 */

import { adminBusinessApi, adminRemindersApi, adminAppointmentsApi } from '@/api';
import { DAYS_OF_WEEK } from './constants';
import type { OnboardingDraft } from './types';

export interface ApplyStepResult {
  step: 'hours' | 'services' | 'reminders' | 'appointment' | 'reschedule' | 'cancellation';
  ok: boolean;
  error?: string;
  /** Self-service manage link for a just-created appointment (appointment step only). */
  manageUrl?: string;
}

export interface ApplyDraftResult {
  results: ApplyStepResult[];
  ok: boolean;
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Request failed';
}

export async function applyHours(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyStepResult> {
  try {
    // draft.businessHours may only cover the days actually being changed (e.g. a
    // concierge chat that only proposed Monday's hours) — the update endpoint
    // requires all 7, so fill any day not in the draft from the business's
    // current hours rather than guessing or crashing on a missing key.
    const current = await adminBusinessApi.getBusinessHours(businessId);
    const currentByDayOfWeek = new Map(current.businessHours.map((h) => [h.dayOfWeek, h]));

    const businessHours = DAYS_OF_WEEK.map((day, index) => {
      const dayOfWeek = (index + 1) % 7;
      const proposed = draft.businessHours[day];
      if (proposed) {
        return {
          dayOfWeek,
          isOpen: !proposed.closed,
          openTime: proposed.open,
          closeTime: proposed.close,
        };
      }
      return (
        currentByDayOfWeek.get(dayOfWeek) ?? {
          dayOfWeek,
          isOpen: false,
          openTime: '09:00',
          closeTime: '17:00',
        }
      );
    });

    await adminBusinessApi.updateBusinessHours(businessId, { businessHours });
    return { step: 'hours', ok: true };
  } catch (error) {
    return { step: 'hours', ok: false, error: toErrorMessage(error) };
  }
}

export async function applyServices(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyStepResult> {
  if (draft.services.length === 0) {
    return { step: 'services', ok: true };
  }
  try {
    const existing = await adminBusinessApi.getServices(businessId);
    const existingNames = new Set(existing.services.map((s) => s.name.trim().toLowerCase()));

    for (const [index, service] of draft.services.entries()) {
      const name = service.name.en.trim();
      if (existingNames.has(name.toLowerCase())) continue;
      await adminBusinessApi.createService(businessId, {
        name,
        durationMinutes: service.durationMinutes,
        price: service.price,
        type: service.type ?? 'primary',
        durationBehavior: service.durationBehavior ?? 'additive',
        description: service.description?.en,
        category: service.category?.en,
        sortOrder: index,
      });
      existingNames.add(name.toLowerCase());
    }
    return { step: 'services', ok: true };
  } catch (error) {
    return { step: 'services', ok: false, error: toErrorMessage(error) };
  }
}

export async function applyReminders(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyStepResult> {
  try {
    // The real endpoint requires the full policy shape; fetch current values for
    // fields the wizard doesn't collect (confirmationEnabled, channel, templateIds)
    // and only override what the wizard actually asked about.
    const current = await adminRemindersApi.getPolicy(businessId);
    await adminRemindersApi.updatePolicy(businessId, {
      confirmationEnabled: current.policy.confirmationEnabled,
      reminderEnabled: draft.reminderEnabled,
      reminderOffsetMinutes: draft.reminderHours * 60,
      channel: current.policy.channel,
      templateIds: current.policy.templateIds,
    });
    return { step: 'reminders', ok: true };
  } catch (error) {
    return { step: 'reminders', ok: false, error: toErrorMessage(error) };
  }
}

export async function applyAppointment(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyStepResult> {
  if (!draft.appointment) {
    return { step: 'appointment', ok: true };
  }
  try {
    const result = await adminAppointmentsApi.createAppointment(businessId, {
      clientId: draft.appointment.clientId,
      clientName: draft.appointment.clientName,
      clientPhone: draft.appointment.clientPhone,
      serviceId: draft.appointment.serviceId,
      providerId: draft.appointment.providerId,
      startTime: draft.appointment.startTime,
      notes: draft.appointment.notes,
    });
    return { step: 'appointment', ok: true, manageUrl: result.manageUrl };
  } catch (error) {
    return { step: 'appointment', ok: false, error: toErrorMessage(error) };
  }
}

export async function applyReschedule(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyStepResult> {
  if (!draft.reschedule) {
    return { step: 'reschedule', ok: true };
  }
  try {
    await adminAppointmentsApi.updateAppointment(businessId, draft.reschedule.appointmentId, {
      startTime: draft.reschedule.startTime,
    });
    return { step: 'reschedule', ok: true };
  } catch (error) {
    return { step: 'reschedule', ok: false, error: toErrorMessage(error) };
  }
}

export async function applyCancellation(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyStepResult> {
  if (!draft.cancellation) {
    return { step: 'cancellation', ok: true };
  }
  try {
    await adminAppointmentsApi.deleteAppointment(businessId, draft.cancellation.appointmentId);
    return { step: 'cancellation', ok: true };
  } catch (error) {
    return { step: 'cancellation', ok: false, error: toErrorMessage(error) };
  }
}

export async function applyDraft(
  businessId: string,
  draft: OnboardingDraft
): Promise<ApplyDraftResult> {
  const results = [
    await applyHours(businessId, draft),
    await applyServices(businessId, draft),
    await applyReminders(businessId, draft),
  ];
  return { results, ok: results.every((r) => r.ok) };
}
